import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useAuthCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { formatDate, capitalize } from '../utils/helpers';
import api from '../utils/api';
import { toast } from 'react-toastify';
import CustomerChatBubble from '../components/CustomerChatBubble';
import PasswordInput from '../components/ui/PasswordInput';
import { Icon } from '../components/ui/Icon';

// NOTE Dev shortcut: cards are captured directly into our DB instead of through
// Stripe. Replace with Stripe Elements before production.

// ── Order history: forest-green theme ───────────────────────────────────────
const FOREST = '#2d6a4f';

const TRACKING_STATUS_LABEL = {
  unknown:              { label: 'Unknown',             dot: '#9ca3af' },
  pre_transit:          { label: 'Pre-Transit',         dot: '#f59e0b' },
  in_transit:           { label: 'In Transit',          dot: '#f59e0b' },
  out_for_delivery:     { label: 'Out for Delivery',    dot: '#3b82f6' },
  delivered:            { label: 'Delivered',           dot: FOREST },
  available_for_pickup: { label: 'Available for Pickup', dot: '#8b5cf6' },
  return_to_sender:     { label: 'Return to Sender',    dot: '#ef4444' },
  failure:              { label: 'Delivery Issue',      dot: '#ef4444' },
  cancelled:            { label: 'Cancelled',           dot: '#9ca3af' },
  error:                { label: 'Error',               dot: '#ef4444' },
};

/** Top-down aerial / satellite-style view of a house roof */
function AerialHouseMap() {
  return (
    <svg
      viewBox="0 0 140 96"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ width: 140, height: 96, borderRadius: 8, display: 'block', flexShrink: 0 }}
    >
      {/* Yard / grass */}
      <rect width="140" height="96" fill="#4a7c59" />
      {/* Driveway */}
      <polygon points="57,74 83,74 98,96 42,96" fill="#c0b8a4" />
      <polygon points="64,74 76,74 82,96 58,96" fill="#b4ac98" />
      {/* Shadow */}
      <rect x="30" y="22" width="82" height="54" rx="2" fill="rgba(0,0,0,0.18)" />
      {/* Back roof panel (shadow side) */}
      <rect x="27" y="19" width="86" height="28" rx="1" fill="#c4b08a" />
      {/* Front roof panel (sunlit) */}
      <rect x="27" y="47" width="86" height="28" rx="1" fill="#d4c29a" />
      {/* Ridge + eave outlines */}
      <line x1="27" y1="47" x2="113" y2="47" stroke="#a08858" strokeWidth="1.5" />
      <rect x="27" y="19" width="86" height="56" rx="1" fill="none" stroke="#a08858" strokeWidth="0.8" />
      {/* Chimney */}
      <rect x="47" y="13" width="13" height="14" rx="1" fill="#8b6b56" />
      <rect x="45" y="11" width="17" height="5" rx="1" fill="#7a5f4a" />
      {/* Back window */}
      <rect x="62" y="25" width="16" height="10" rx="1" fill="#9dc4d0" />
      <line x1="70" y1="25" x2="70" y2="35" stroke="#7aaab8" strokeWidth="0.6" />
      <line x1="62" y1="30" x2="78" y2="30" stroke="#7aaab8" strokeWidth="0.6" />
      {/* Front windows */}
      <rect x="37" y="53" width="14" height="9" rx="1" fill="#9dc4d0" />
      <line x1="44" y1="53" x2="44" y2="62" stroke="#7aaab8" strokeWidth="0.5" />
      <line x1="37" y1="57.5" x2="51" y2="57.5" stroke="#7aaab8" strokeWidth="0.5" />
      <rect x="89" y="53" width="14" height="9" rx="1" fill="#9dc4d0" />
      <line x1="96" y1="53" x2="96" y2="62" stroke="#7aaab8" strokeWidth="0.5" />
      <line x1="89" y1="57.5" x2="103" y2="57.5" stroke="#7aaab8" strokeWidth="0.5" />
      {/* Front door */}
      <rect x="62" y="59" width="16" height="14" rx="1" fill="#9a7760" />
      <circle cx="76" cy="66" r="1.5" fill="#c4a078" />
      {/* Trees — four corners */}
      <circle cx="14" cy="13" r="12" fill="#295c38" /><circle cx="14" cy="13" r="8" fill="#336e46" />
      <circle cx="126" cy="13" r="11" fill="#295c38" /><circle cx="126" cy="13" r="7" fill="#336e46" />
      <circle cx="13" cy="82" r="9" fill="#295c38" /><circle cx="13" cy="82" r="5.5" fill="#336e46" />
      <circle cx="127" cy="82" r="8" fill="#295c38" /><circle cx="127" cy="82" r="5" fill="#336e46" />
      {/* Location pin */}
      <path d="M70 22 C65 22,60 27,60 33 C60 41,70 51,70 51 C70 51,80 41,80 33 C80 27,75 22,70 22Z"
        fill={FOREST} stroke="white" strokeWidth="1.5" />
      <circle cx="70" cy="33" r="4" fill="white" />
    </svg>
  );
}

function TrackingSection({ order, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);
  if (!order.trackingNumber) return null;
  const te = order.trackingEvents;
  const statusInfo = te?.status
    ? (TRACKING_STATUS_LABEL[te.status] || { label: capitalize(te.status), dot: '#9ca3af' })
    : null;
  const latestEvent = te?.events?.[te.events.length - 1];

  const relativeTime = (iso) => {
    if (!iso) return '';
    const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const res = await api.post(`/orders/${order.id}/refresh-tracking`);
      onRefreshed?.(order.id, res.data.trackingEvents, res.data.status);
      if (res.data.cached) toast.info('Already up to date — checked moments ago.');
      else if (res.data.updated) toast.success('Tracking updated.');
      else toast.info('No new tracking scan available yet.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not refresh tracking right now.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(212,175,55,0.2)' }}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon name="orders" className="w-4 h-4" style={{ color: '#d4af37' }} />
        {order.carrier && (
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#7a5c30' }}>{order.carrier}</span>
        )}
        <span className="font-mono text-xs px-2 py-0.5 rounded tracking-wide"
          style={{ background: 'rgba(212,175,55,0.1)', color: '#d4af37', border: '1px solid rgba(212,175,55,0.2)' }}>
          {order.trackingNumber}
        </span>
        {statusInfo && (
          <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: statusInfo.dot }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusInfo.dot, display: 'inline-block', flexShrink: 0 }} />
            {statusInfo.label}
          </span>
        )}
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="ml-auto text-xs font-semibold inline-flex items-center gap-1"
          style={{ color: '#d4af37', opacity: refreshing ? 0.5 : 1, cursor: refreshing ? 'default' : 'pointer' }}
          title="Check the carrier for the latest scan"
        >
          <Icon name="refresh" className={'w-3.5 h-3.5' + (refreshing ? ' animate-spin' : '')} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {te?.publicUrl && (
          <a href={te.publicUrl} target="_blank" rel="noopener noreferrer"
            className="text-xs font-semibold" style={{ color: '#d4af37' }}>
            Track &rarr;
          </a>
        )}
      </div>
      {latestEvent && (
        <div className="mt-1.5 text-xs flex items-start gap-1.5" style={{ color: '#7a5c30' }}>
          <span style={{ color: '#d4af37', marginTop: 2, flexShrink: 0 }}>●</span>
          <span>
            <span className="font-medium" style={{ color: '#a07840' }}>{latestEvent.description}</span>
            {latestEvent.location && <span style={{ color: '#5a3a18' }}> — {latestEvent.location}</span>}
          </span>
        </div>
      )}
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        {te?.estimatedDelivery && (
          <span className="text-xs" style={{ color: '#5a3a18' }}>
            Est. delivery:{' '}
            <strong style={{ color: '#7a5c30' }}>
              {new Date(te.estimatedDelivery).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
            </strong>
          </span>
        )}
        {te?.queriedAt && (
          <span className="text-[10px]" style={{ color: '#3a2510' }}>Updated {relativeTime(te.queriedAt)}</span>
        )}
      </div>
    </div>
  );
}


// ── Per-order message thread (admin responses to a single order) ─────────────
function OrderMessages({ orderId }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders/" + orderId + "/messages");
      setMessages(res.data.messages || []);
    } catch {
      toast.error("Failed to load messages");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && messages === null) load();
  };

  const label = (m) =>
    m.senderType === "admin"  ? "✦ AB8 Beauty Team" :
    m.senderType === "system" ? "System"          : "You";

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(212,175,55,0.15)" }}>
      <button
        type="button"
        onClick={toggle}
        className="flex items-center gap-2 text-xs font-bold transition-opacity hover:opacity-80"
        style={{ color: "#d4af37" }}
      >
        <Icon name="chat" className="w-4 h-4" />
        <span>Messages from our team{messages ? " (" + messages.length + ")" : ""}</span>
        <span className="text-[9px]">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
          {loading ? (
            <div className="text-xs" style={{ color: "#7a5c30" }}>Loading…</div>
          ) : !messages || messages.length === 0 ? (
            <div className="text-xs" style={{ color: "#7a5c30" }}>
              No messages yet on this order.
            </div>
          ) : (
            messages.map(m => (
              <div
                key={m.id}
                className="rounded-lg px-3 py-2"
                style={{
                  background: m.senderType === "admin" ? "rgba(212,175,55,0.10)" : "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(212,175,55,0.12)",
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: m.senderType === "admin" ? "#d4af37" : "#7a5c30" }}
                  >
                    {label(m)}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words" style={{ color: "#e8d9b8" }}>
                  {m.body}
                </div>
                <div className="text-[9px] mt-1" style={{ color: "#5a3a18" }}>
                  {new Date(m.createdAt).toLocaleString("en-CA", {
                    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}


// ── Admin-only: change password without email verification ──────────────────
function AdminSecurityCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving]                   = useState(false);

  const strongEnough = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword);
  const canSubmit = currentPassword && strongEnough && newPassword === confirmPassword && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      toast.success('Password updated.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tea-500';

  return (
    <div className="mt-6 pt-6 border-t border-gray-100 text-left">
      <h3 className="text-sm font-bold text-gray-700 mb-1 inline-flex items-center gap-1.5">
        <Icon name="lock" className="w-4 h-4 text-tea-600" />
        Change password
      </h3>
      <p className="text-xs text-gray-500 mb-3">Update your admin password directly — no email verification needed.</p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <PasswordInput autoComplete="current-password" placeholder="Current password"
          value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className={inputCls} />
        <PasswordInput autoComplete="new-password" placeholder="New password"
          value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputCls} />
        <PasswordInput autoComplete="new-password" placeholder="Confirm new password"
          value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputCls} />
        {newPassword && !strongEnough && (
          <p className="text-[11px] text-amber-600">Min 8 characters, one uppercase letter and one number.</p>
        )}
        {confirmPassword && newPassword !== confirmPassword && (
          <p className="text-[11px] text-red-600">Passwords don&apos;t match.</p>
        )}
        <button type="submit" disabled={!canSubmit}
          className="btn-primary w-full text-sm disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

// ── Text My Client: SMS opt-in panel ───────────────────────────────────────
function SmsUpdatesPanel() {
  const [phone, setPhone]           = useState('');
  const [textUpdates, setText]      = useState(false);
  const [vipUpdates, setVip]        = useState(false);
  const [providerLive, setProvider] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [testing, setTesting]       = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/sms/preferences');
        if (!alive) return;
        setPhone(data.smsPhone || '');
        setText(!!data.textUpdates);
        setVip(!!data.vipUpdates);
        setProvider(!!data.providerLive);
      } catch (_) {
        /* not logged in / not available — leave panel in default state */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async (next = {}) => {
    setSaving(true);
    try {
      const payload = {
        phone: next.phone !== undefined ? next.phone : phone,
        textUpdates: next.textUpdates !== undefined ? next.textUpdates : textUpdates,
        vipUpdates: next.vipUpdates !== undefined ? next.vipUpdates : vipUpdates,
      };
      const { data } = await api.put('/sms/preferences', payload);
      setPhone(data.smsPhone || '');
      setText(!!data.textUpdates);
      setVip(!!data.vipUpdates);
      setProvider(!!data.providerLive);
      toast.success('Text preferences saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save text preferences.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (which) => {
    const nextVal = which === 'text' ? !textUpdates : !vipUpdates;
    if (which === 'text') setText(nextVal); else setVip(nextVal);
    save({ [which === 'text' ? 'textUpdates' : 'vipUpdates']: nextVal });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/sms/test');
      toast.info(data.message || 'Test queued.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send test.');
    } finally {
      setTesting(false);
    }
  };

  const Row = ({ on, onClick, emoji, title, desc }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      className="w-full flex items-center gap-3 text-left rounded-xl px-3 py-2.5 transition disabled:opacity-50"
      style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)' }}
    >
      <span className="shrink-0 inline-flex">{emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: '#e8c97a' }}>{title}</div>
        <div className="text-[11px] leading-snug" style={{ color: '#9a8050' }}>{desc}</div>
      </div>
      <span
        className="shrink-0 rounded-full transition-all"
        style={{
          width: 38, height: 22, padding: 2,
          background: on ? '#4ade80' : 'rgba(255,255,255,0.15)',
          display: 'inline-flex', justifyContent: on ? 'flex-end' : 'flex-start',
        }}
      >
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff' }} />
      </span>
    </button>
  );

  if (loading) {
    return <div className="mb-4 rounded-2xl animate-pulse h-32" style={{ background: '#150d33' }} />;
  }

  return (
    <div
      className="mb-5 rounded-2xl p-4"
      style={{ background: 'linear-gradient(145deg, #0a0518, #160c34)', border: '1px solid rgba(212,175,55,0.28)' }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon name="chat" className="w-5 h-5" style={{ color: '#f0d488' }} />
        <h3 className="text-sm font-bold" style={{ color: '#f0d488' }}>Text me updates</h3>
        {!providerLive && (
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#9a8050' }}>
            preview
          </span>
        )}
      </div>
      <p className="text-[11px] mb-3" style={{ color: '#8a7048' }}>
        Choose what we text you. Standard message rates apply. Reply STOP anytime to opt out.
      </p>

      <input
        type="tel"
        inputMode="tel"
        placeholder="Mobile number (e.g. 555 123 4567)"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onBlur={() => { if (phone) save({ phone }); }}
        className="w-full rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none"
        style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(212,175,55,0.25)', color: '#f5e9c8' }}
      />

      <div className="space-y-2">
        <Row
          on={textUpdates}
          onClick={() => toggle('text')}
          emoji={<Icon name="orders" className="w-5 h-5" style={{ color: '#e8c97a' }} />}
          title="Text Updates"
          desc="Order confirmations, shipping notifications & delivery updates."
        />
        <Row
          on={vipUpdates}
          onClick={() => toggle('vip')}
          emoji={<Icon name="star" className="w-5 h-5" style={{ color: '#e8c97a' }} />}
          title="VIP Updates"
          desc="Early access to drops and VIP-only offers."
        />
      </div>

      {(textUpdates || vipUpdates) && phone && (
        <button
          type="button"
          onClick={sendTest}
          disabled={testing}
          className="mt-3 text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
          style={{ border: '1px solid rgba(74,222,128,0.35)', color: '#4ade80' }}
        >
          {testing ? 'Sending…' : 'Send me a test text'}
        </button>
      )}
    </div>
  );
}

// ── Main Account page ───────────────────────────────────────────────────────
export default function AccountPage() {
  const { user } = useAuthCtx();
  const { fmt } = useCurrency();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get('/orders')
      .then(res => setOrders(res.data.data))
      .catch(() => toast.error('Failed to load orders'));
  }, []);

  // Merge a freshly-pulled tracking payload back into the rendered order.
  const handleTrackingRefreshed = (orderId, trackingEvents, status) => {
    setOrders(prev => prev.map(o =>
      o.id === orderId
        ? { ...o, trackingEvents: trackingEvents || o.trackingEvents, status: status || o.status }
        : o
    ));
  };

  // Live chat bubble binds to the customer's most recent order.
  const chatOrder = orders.length
    ? [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 py-12 w-full">
        <h1 className="text-3xl font-bold mb-8">My Account</h1>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">

          {/* Profile card */}
          <div className="card md:col-span-1 h-fit">
            <div className="text-center">
              <div className="w-16 h-16 bg-tea-100 rounded-full flex items-center justify-center text-2xl font-bold text-tea-700 mx-auto mb-4">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <p className="font-bold text-lg">{user?.firstName} {user?.lastName}</p>
              <p className="text-gray-500 text-sm">{user?.email}</p>
              <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                <span className={'inline-block ' + (user?.role === 'affiliate' ? 'badge-green' : 'badge-gray')}>
                  {capitalize(user?.role || '')}
                </span>
                {user?.tier === 'platinum' && (
                  <span className="inline-block bg-slate-900 text-amber-300 text-[10px] font-bold uppercase tracking-wide rounded px-2 py-0.5">
                    Platinum
                  </span>
                )}
              </div>

              {user?.walletBalanceCents !== undefined && (
                <div className="mt-4 rounded-lg bg-tea-50 border border-tea-100 p-3">
                  <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold inline-flex items-center gap-1.5">
                    <Icon name="commissions" className="w-4 h-4 text-pink-600" />
                    Glow Cash
                  </div>
                  <div className="text-xl font-bold text-tea-700 mt-1">{fmt(user.walletBalanceCents)}</div>
                  <div className="text-[11px] text-gray-500 mt-1">Earn 1% back on every order, plus referral cash. Applies at checkout — shipping is still charged.</div>
                </div>
              )}
            </div>

            {user?.role === 'customer' && (
              <button
                onClick={async () => {
                  try {
                    await api.post('/affiliates/upgrade');
                    toast.success('You are now an affiliate!');
                    window.location.reload();
                  } catch {
                    toast.error('Failed to upgrade');
                  }
                }}
                className="btn-secondary w-full mt-6 text-sm">
                Join Affiliate Program
              </button>
            )}

            {user?.role === 'affiliate' && (
              <Link to="/affiliate" className="btn-primary w-full mt-6 text-sm block text-center">
                Affiliate Dashboard
              </Link>
            )}

            {user?.role === 'admin' && <AdminSecurityCard />}
          </div>

          {/* Right column — order history */}
          <div className="md:col-span-3 space-y-6">

            {/* Text My Client — SMS opt-in choices */}
            <SmsUpdatesPanel />

            {/* ── Order History ────────────────────────────────────────── */}
            <div>
              <h2 className="text-xl font-bold mb-4">Order History</h2>
              {orders.length === 0 ? (
                <div className="rounded-2xl text-center py-10" style={{
                  background: 'linear-gradient(145deg, #070412, #130b30)',
                  border: '1px solid rgba(212,175,55,0.2)',
                }}>
                  <div className="flex justify-center mb-3">
                    <Icon name="orders" className="w-12 h-12" style={{ color: '#a07840' }} />
                  </div>
                  <p className="font-semibold" style={{ color: '#a07840' }}>No orders yet</p>
                  <p className="text-sm mt-1" style={{ color: '#5a3a18' }}>
                    Browse our catalog to place your first order.
                  </p>
                  <Link to="/shop" className="btn-primary mt-5 inline-block text-sm">Browse Catalog</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div
                      key={order.id}
                      className="relative rounded-2xl p-5 overflow-hidden"
                      style={{
                        background: 'linear-gradient(145deg, #070412 0%, #130b30 45%, #070412 100%)',
                        border: '2px solid rgba(212,175,55,0.4)',
                      }}
                    >
                      {/* Status badge — top left */}
                      <div className="absolute top-3.5 left-4">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          order.status === 'delivered' ? 'bg-green-900/60 text-green-400' :
                          order.status === 'shipped'   ? 'bg-blue-900/60 text-blue-400' :
                          order.status === 'processing' ? 'bg-yellow-900/60 text-yellow-400' :
                          order.status === 'cancelled' ? 'bg-red-900/60 text-red-400' :
                          'bg-gray-800 text-gray-400'
                        }`}>
                          {capitalize(order.status)}
                        </span>
                      </div>

                      {/* Order # badge — top right */}
                      <div
                        className="absolute top-3.5 right-4 text-[9px] font-black tracking-[0.15em] px-2 py-0.5 rounded"
                        style={{
                          background: 'linear-gradient(135deg, #b8860b, #d4af37, #f5c842)',
                          color: '#0a0718',
                        }}
                      >
                        #{order.id.slice(0, 8).toUpperCase()}
                      </div>

                      {/* Order content */}
                      <div className="mt-6 mb-4">
                        <div className="font-bold text-xl" style={{ color: '#f5e6c8' }}>
                          {order.items?.map(item => item.variant?.product?.name).filter(Boolean).join(', ')}
                        </div>
                        <div className="text-sm mt-1" style={{ color: '#7a5c30' }}>
                          {order.items?.map(item => `${item.variant?.name || item.variant?.product?.name} × ${item.quantity}`).join(' · ')}
                        </div>
                        <div className="flex items-baseline gap-3 mt-3 flex-wrap">
                          <span className="text-2xl font-black" style={{ color: '#d4af37' }}>
                            {fmt(order.totalInCents)}
                          </span>
                          <span className="text-xs" style={{ color: '#7a5c30' }}>
                            {formatDate(order.createdAt)}
                          </span>
                        </div>
                      </div>

                      <TrackingSection order={order} onRefreshed={handleTrackingRefreshed} />

                      <OrderMessages orderId={order.id} />
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </main>
      <Footer />
      {chatOrder && (
        <CustomerChatBubble
          orderId={chatOrder.id}
          orderLabel={'#' + chatOrder.id.slice(0, 8).toUpperCase()}
        />
      )}
    </div>
  );
}
